import React from "react";
import { Search, Target, Repeat, Layers, RefreshCw } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const STEPS = [
  {
    key: "assess",
    icon: Search,
    number: "01",
    title: "Assess the State",
    body:
      "A baseline of the physiology driving current behavior. HRV, autonomic tone, sleep architecture, stress load, movement capacity, engagement, and behavioral markers are captured together.",
    output: "A physiological + behavioral baseline that becomes the loop's first input.",
  },
  {
    key: "prescribe",
    icon: Target,
    number: "02",
    title: "Prescribe the Stimulus",
    body:
      "Precisely dosed exercise physiology, movement, and environmental inputs are prescribed to shift — not exhaust — the nervous system. Load, timing, and modality are matched to the observed state.",
    output: "A personalized weekly cadence of physiological inputs.",
  },
  {
    key: "reinforce",
    icon: Repeat,
    number: "03",
    title: "Reinforce the Signal",
    body:
      "Repetition builds adaptation. Accountability structures, environmental cues, and behavioral conditioning make sure the prescribed signal is delivered consistently — the condition under which the nervous system actually adapts.",
    output: "Consistent signal delivery, week over week.",
  },
  {
    key: "retrain",
    icon: Layers,
    number: "04",
    title: "Retrain the Default",
    body:
      "The physiological set-point shifts. New behavior begins to emerge as the output of a new state — no longer requiring willpower to produce. This is when the methodology is doing its actual work.",
    output: "A measurably different physiological default.",
  },
  {
    key: "reassess",
    icon: RefreshCw,
    number: "05",
    title: "Reassess & Adapt",
    body:
      "Markers are re-measured. Progress and regression are read together. The prescription is adapted — the same protocol never runs twice against a system that has already changed.",
    output: "The loop closes — and immediately re-opens at a new baseline.",
  },
];

const HowItWorks = () => {
  return (
    <PageShell>
      <SubHero
        testId="how-hero"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Methodology", to: "/methodology" },
          { label: "How It Works" },
        ]}
        overline="How It Works"
        headline="A closed-loop protocol,"
        headlineAccent="not a linear program."
        lead="Adaptive Physiology™ is delivered as five continuously repeating movements. Each movement produces measurable signal; each signal informs how the next is delivered. The loop closes — and the physiology adapts."
      />

      <section
        data-testid="how-loop-diagram-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div
              data-testid="how-loop-diagram"
              className="rounded-[24px] border border forr-surface p-8 sm:p-10"
            >
              <div className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep mb-6">
                The Loop
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 forr-text-ink font-display text-lg sm:text-xl md:text-2xl font-medium">
                <span>Assess</span>
                <span className="forr-text-teal-deep">→</span>
                <span>Prescribe</span>
                <span className="forr-text-teal-deep">→</span>
                <span>Reinforce</span>
                <span className="forr-text-teal-deep">→</span>
                <span>Retrain</span>
                <span className="forr-text-teal-deep">→</span>
                <span>Reassess</span>
                <span className="forr-text-teal-deep">↻</span>
              </div>
              <p className="mt-6 text-sm forr-text-soft font-light max-w-2xl">
                Each cycle typically runs on a weekly cadence, with reassessment
                windows at 4, 8, and 12 weeks. The methodology re-adapts every
                cycle — it does not repeat.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section
        data-testid="how-steps-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="→"
              label="The Five Movements"
              testId="how-steps-overline"
            />
          </Reveal>

          <div className="mt-16 space-y-6">
            {STEPS.map((s, i) => (
              <Reveal delay={i * 0.06} key={s.key}>
                <article
                  data-testid={`how-step-${s.key}`}
                  className="rounded-[24px] border border forr-surface p-8 sm:p-10 hover:border-teal-soft transition-colors duration-500"
                >
                  <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-4">
                      <div className="flex items-center gap-4">
                        <span className="font-display forr-text-teal-deep text-3xl font-medium tracking-tight">
                          {s.number}
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep">
                          <s.icon size={18} strokeWidth={1.5} />
                        </div>
                      </div>
                      <h2 className="mt-6 font-display forr-text-ink text-2xl sm:text-3xl font-medium tracking-[-0.02em] leading-tight">
                        {s.title}
                      </h2>
                    </div>
                    <div className="col-span-12 md:col-span-8 md:pl-8 md:border-l md:border">
                      <p className="text-lg forr-text-ink font-light leading-relaxed">
                        {s.body}
                      </p>
                      <div className="mt-8 flex items-start gap-3 text-sm">
                        <span className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep shrink-0 mt-1">
                          Output
                        </span>
                        <span className="forr-text-soft font-light leading-relaxed">
                          {s.output}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="how-cadence-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="06"
                  label="Cadence"
                  testId="how-cadence-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Delivered on a rhythm the nervous system responds to.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <div className="space-y-6">
                {[
                  {
                    k: "Daily",
                    v: "Prescribed inputs and behavioral reinforcement.",
                  },
                  {
                    k: "Weekly",
                    v: "Signal review and prescription adjustment.",
                  },
                  {
                    k: "Monthly",
                    v: "Marker reassessment and protocol adaptation.",
                  },
                  {
                    k: "Quarterly",
                    v: "State review — is the physiological default shifting?",
                  },
                ].map((c, i) => (
                  <Reveal delay={i * 0.05} key={c.k}>
                    <div
                      data-testid={`how-cadence-${c.k.toLowerCase()}`}
                      className="flex items-start gap-8 border-b border pb-6"
                    >
                      <div className="w-24 shrink-0 forr-text-teal-deep font-display text-lg font-medium">
                        {c.k}
                      </div>
                      <div className="forr-text-soft font-light leading-relaxed">
                        {c.v}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NextStep
        overline="Next"
        title="Outcomes"
        description="What the methodology measurably shifts — in physiology, in behavior, and in long-term capacity."
        to="/methodology/outcomes"
        cta="See outcomes"
        testId="how-next-step"
      />
    </PageShell>
  );
};

export default HowItWorks;
